import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { productService } from '../../services/productService';
import { productCategoryService } from '../../services/productCategoryService';
import { ProductCard } from './ProductCard';
import { ProductFormModal } from './ProductFormModal';
import { ProductCategoryManager } from './ProductCategoryManager';
import { ConfirmationModal } from '../common/ConfirmationModal';
import { copyProductLink } from '../../utils/deepLinks';
import type { Product, ProductCategory } from '../../types/product';
import type { Facility } from '../../types/facility';

interface ProductsSectionProps {
  businessProfileId?: string;
  facilities?: Facility[];
  isCollapsed?: boolean;
  onToggle?: () => void;
  /** Render without the collapsible header — used inside ProductsPanel. */
  hideHeader?: boolean;
}

const UNCATEGORIZED_KEY = '__uncategorized__';

export function ProductsSection({
  businessProfileId,
  facilities = [],
  isCollapsed = false,
  onToggle,
  hideHeader = false,
}: ProductsSectionProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const { showToast } = useToast();

  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [deletingProduct, setDeletingProduct] = useState<Product | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadData = useCallback(async () => {
    if (!token || !businessProfileId) return;

    setIsLoading(true);
    try {
      const [productsData, categoriesData] = await Promise.all([
        productService.getProducts(businessProfileId, token),
        productCategoryService.getCategories(businessProfileId, token),
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load products';
      showToast('error', t('common.error'), message);
    } finally {
      setIsLoading(false);
    }
  }, [token, businessProfileId, showToast, t]);

  // Load data on mount to show count in header
  useEffect(() => {
    if (businessProfileId) {
      loadData();
    }
  }, [businessProfileId, loadData]);

  // Products already arrive sorted (category order, then item order) from the
  // API — this just buckets them, it does not re-sort.
  const productsByCategory = useMemo(() => {
    const map = new Map<string, Product[]>();
    for (const product of products) {
      const key = product.productCategoryId || UNCATEGORIZED_KEY;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(product);
    }
    return map;
  }, [products]);

  const uncategorized = productsByCategory.get(UNCATEGORIZED_KEY) || [];

  const handleAdd = () => {
    setEditingProduct(null);
    setIsFormOpen(true);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setIsFormOpen(true);
  };

  const handleDelete = (product: Product) => {
    setDeletingProduct(product);
  };

  const handleCopyLink = async (product: Product) => {
    const ok = await copyProductLink(product.id);
    if (ok) {
      showToast('success', t('common.linkCopied'));
    } else {
      showToast('error', t('common.linkCopyError'));
    }
  };

  const confirmDelete = async () => {
    if (!token || !businessProfileId || !deletingProduct) return;

    setIsDeleting(true);
    try {
      await productService.deleteProduct(businessProfileId, deletingProduct.id, token);
      showToast('success', t('products.deleteSuccess'));
      setProducts(prev => prev.filter(p => p.id !== deletingProduct.id));
      setDeletingProduct(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete product';
      showToast('error', t('common.error'), message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setEditingProduct(null);
    loadData();
  };

  // SW-94: persists a new item order within one category. Cross-category
  // moves go through the product form's category selector instead of drag
  // (dragging a product between category sections isn't supported — only
  // reordering within a category, and reordering categories themselves).
  const handleProductReorder = async (categoryId: string | null, orderedIds: string[]) => {
    if (!token || !businessProfileId) return;

    // Cross-category relative order in the flat `products` array is never
    // read (rendering always re-buckets via productsByCategory), so it's
    // enough to just splice in the new within-category order.
    const previous = products;
    setProducts(prev => {
      const key = categoryId || UNCATEGORIZED_KEY;
      const others = prev.filter(p => (p.productCategoryId || UNCATEGORIZED_KEY) !== key);
      const reorderedGroup = orderedIds
        .map(id => prev.find(p => p.id === id))
        .filter((p): p is Product => !!p);
      return [...others, ...reorderedGroup];
    });

    try {
      await productService.reorderProducts(businessProfileId, categoryId, orderedIds, token);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save item order';
      showToast('error', t('common.error'), message);
      setProducts(previous);
    }
  };

  const totalCount = products.length;

  return (
    <div className="bg-brand-primary-deep rounded-lg overflow-hidden">
      {/* Section Header */}
      {!hideHeader && (
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/10 transition-colors"
        >
          <div className="flex items-center gap-3">
            <svg className="w-5 h-5 text-brand-confirm" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <span className="text-lg font-medium text-white">{t('products.title')}</span>
            <span className="text-sm text-white/70">({totalCount})</span>
          </div>
          <svg
            className={`w-5 h-5 text-white/70 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {/* Section Content */}
      {!isCollapsed && (
        <div className={`px-4 pb-4 space-y-4 ${hideHeader ? 'pt-4' : ''}`}>
          {/* Add Button */}
          <button
            onClick={handleAdd}
            disabled={!businessProfileId}
            className="w-full py-3 border-2 border-dashed border-white/30 rounded-lg text-white/80 hover:border-brand-confirm hover:text-brand-confirm transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            {t('products.addProduct')}
          </button>

          {/* Loading State */}
          {isLoading && (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="bg-white/10 rounded-lg p-4 animate-pulse">
                  <div className="h-5 bg-white/20 rounded w-48 mb-2" />
                  <div className="h-4 bg-white/20 rounded w-32" />
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!isLoading && totalCount === 0 && (
            <div className="text-center py-8 text-white/60">
              <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
              <p>{t('products.noProducts')}</p>
            </div>
          )}

          {/* Grouped Products List */}
          {!isLoading && totalCount > 0 && (
            <div className="space-y-5">
              {categories.map(category => {
                const items = productsByCategory.get(category.id) || [];
                if (items.length === 0) return null;
                return (
                  <ProductCategoryGroup
                    key={category.id}
                    categoryId={category.id}
                    categoryName={category.name}
                    products={items}
                    onReorder={orderedIds => handleProductReorder(category.id, orderedIds)}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onCopyLink={handleCopyLink}
                  />
                );
              })}
              {uncategorized.length > 0 && (
                <ProductCategoryGroup
                  categoryId={null}
                  categoryName={t('products.categories.uncategorized', 'Bez kategorii')}
                  products={uncategorized}
                  onReorder={orderedIds => handleProductReorder(null, orderedIds)}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onCopyLink={handleCopyLink}
                />
              )}
            </div>
          )}

          {/* Categories — edited occasionally, so they sit at the end */}
          {!isLoading && businessProfileId && (
            <ProductCategoryManager
              businessProfileId={businessProfileId}
              categories={categories}
              onCategoriesChange={setCategories}
            />
          )}
        </div>
      )}

      {/* Form Modal */}
      {isFormOpen && businessProfileId && (
        <ProductFormModal
          product={editingProduct}
          businessProfileId={businessProfileId}
          facilities={facilities}
          categories={categories}
          onClose={handleFormClose}
          onSuccess={handleFormSuccess}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingProduct && (
        <ConfirmationModal
          title={t('products.deleteConfirmTitle')}
          message={t('products.deleteConfirmMessage', { name: deletingProduct.title })}
          confirmLabel={t('common.delete')}
          cancelLabel={t('common.cancel')}
          onConfirm={confirmDelete}
          onCancel={() => setDeletingProduct(null)}
          isLoading={isDeleting}
          variant="danger"
        />
      )}
    </div>
  );
}

interface ProductCategoryGroupProps {
  categoryId: string | null;
  categoryName: string;
  products: Product[];
  onReorder: (orderedIds: string[]) => void;
  onEdit: (product: Product) => void;
  onDelete: (product: Product) => void;
  onCopyLink: (product: Product) => void;
}

function ProductCategoryGroup({
  categoryId, categoryName, products, onReorder, onEdit, onDelete, onCopyLink,
}: ProductCategoryGroupProps) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = products.findIndex(p => p.id === active.id);
    const newIndex = products.findIndex(p => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    onReorder(arrayMove(products, oldIndex, newIndex).map(p => p.id));
  };

  return (
    <div>
      <h4 className="text-sm font-semibold text-white/80 uppercase tracking-wide mb-2" data-category-id={categoryId ?? ''}>
        {categoryName}
      </h4>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={products.map(p => p.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {products.map(product => (
              <SortableProductCard
                key={product.id}
                product={product}
                onEdit={() => onEdit(product)}
                onDelete={() => onDelete(product)}
                onCopyLink={() => onCopyLink(product)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}

interface SortableProductCardProps {
  product: Product;
  onEdit: () => void;
  onDelete: () => void;
  onCopyLink: () => void;
}

function SortableProductCard({ product, onEdit, onDelete, onCopyLink }: SortableProductCardProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: product.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-start gap-2">
      <button
        {...attributes}
        {...listeners}
        className="mt-4 cursor-grab active:cursor-grabbing text-white/40 hover:text-white/70 touch-none flex-shrink-0"
        aria-label="Przeciągnij, aby zmienić kolejność"
      >
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M7 4a1 1 0 11-2 0 1 1 0 012 0zM7 10a1 1 0 11-2 0 1 1 0 012 0zM7 16a1 1 0 11-2 0 1 1 0 012 0zM15 4a1 1 0 11-2 0 1 1 0 012 0zM15 10a1 1 0 11-2 0 1 1 0 012 0zM15 16a1 1 0 11-2 0 1 1 0 012 0z" /></svg>
      </button>
      <div className="flex-1 min-w-0">
        <ProductCard product={product} onEdit={onEdit} onDelete={onDelete} onCopyLink={onCopyLink} />
      </div>
    </div>
  );
}
